// @ts-nocheck
import { ChangeEvent, useCallback, useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { KnmInnField } from 'KnmPackage/MainModule/components/KnmInnField';
import { KnmOgrnField } from 'KnmPackage/MainModule/components/KnmOgrnField';
import { useKnmSetFnsLookupLoadingFlags } from 'KnmPackage/MainModule/hooks/useKnmSetFnsLookupLoadingFlags';
import { useKnmSetLegalEntityDataAction } from 'KnmPackage/MainModule/hooks/useKnmSetLegalEntityDataAction';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef, getTargetEntityFounderSelectOptions } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { parsePassport } from 'CorePackage/MainModule/common/parsePassport';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { checkOgrnBySubjectType } from 'CorePackage/MainModule/validators/isOgrn';
import { ICountry } from 'APIPackage/CatalogsModule/interfaces/ICountry';
import { KnmSnilsField } from 'KnmPackage/MainModule/components/KnmSnilsField';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { MAX_CHARACTERS_ERROR, REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IErknmFormAgreementPetitionOrganization } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreementPetitionOrganization';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { RafpRequestStatusTypeEnum } from 'APIPackage/KnmModule/enums/RafpRequestStatusTypeEnum';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import styles from './PetitionOrganization.module.scss';

interface IPetitionOrganizationProps {
  isNewOrganizationTemplate?: boolean;
  isWatchOnly: boolean;
  name: string | undefined;
  parentId: string;
  organization: IErknmFormAgreementPetitionOrganization;
}

interface IPetitionOrganizationStoreProps {
  formRefKnm: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
  isSupervisor: boolean | undefined;
  subjectTypeOptions: IDictionarySelectOption<ITargetEntity>[] | undefined;
}

const PetitionOrganizationWithoutStore = ({
  isNewOrganizationTemplate,
  isWatchOnly,
  name,
  formRefKnm,
  decisionFormRef,
  isSupervisor,
  subjectTypeOptions,
  organization,
  parentId,
}: IPetitionOrganizationProps & IPetitionOrganizationStoreProps) => {
  const { isKnm, isPm, isDecision } = useContext(AgreementsContext);
  const formRef = isDecision ? decisionFormRef : formRefKnm;

  const { isAutocomplete, inn, ogrn, subjectType, kpp, rafpNza, snils } = organization;

  const isFIZ = subjectType?.label === KnmSubjectTypesEnum.FIZ;
  const isIFIZ = subjectType?.label === KnmSubjectTypesEnum.IFIZ;
  const isUR = subjectType?.label === KnmSubjectTypesEnum.UR;
  const isIP = subjectType?.label === KnmSubjectTypesEnum.IP;
  const isFIUR = subjectType?.label === KnmSubjectTypesEnum.FIUR;
  const isIUR = subjectType?.label === KnmSubjectTypesEnum.IUR;

  const { setFnsLookupByOgrnLoadingFlag, setFnsLookupByInnLoadingFlag } = useKnmSetFnsLookupLoadingFlags();
  const { dispatchKnmSetLegalEntityData } = useKnmSetLegalEntityDataAction();

  const ogrnFieldName = `${name}.ogrn`;
  const innFieldName = `${name}.inn`;
  const organizationNameFieldName = `${name}.organizationName`;
  const subjectTypeFieldName = `${name}.subjectType`;
  const kppFieldName = `${name}.kpp`;
  const rafpNzaFieldName = `${name}.rafpNza`;
  const countryFieldName = `${name}.country`;
  const locationAddressFieldName = `${name}.locationAddress`;
  const workAddressFieldName = `${name}.workAddress`;
  const snilsFieldName = `${name}.snils`;
  const documentFieldName = `${name}.document`;
  const isAutocompleteFieldName = `${name}.isAutocomplete`;

  const rafpNotFoundMessage =
    'Запрашиваемые сведения отсутствуют в РАФП, проверьте введенные значения. Если у данного иностранного ' +
    'юридического лица нет филиалов и представительств, зарегистрированных на территории РФ, ' +
    'то необходимо выбрать другой тип контролируемого лица – Иностранные юридические лица';

  const [rafpNzaNotFoundText, setRafpNzaNotFoundText] = useState<string | undefined>(rafpNotFoundMessage);

  const handleLegalEntityAutoCompleteSelect = useCallback(
    (legalEntityLookup: ILegalEntityLookup) => {
      if (!isIUR) {
        const {
          ogrn: legalEntityOgrn,
          inn: legalEntityInn,
          orgName: legalEntityOrgName,
          leAddress: legalEntityLeAddress,
          workAddress: legalEntityWorkAddress,
        } = legalEntityLookup;

        formRef?.current.change(`${name}.organizationAutocomplete`, true);
        if (legalEntityOgrn) {
          formRef?.current.change(ogrnFieldName, legalEntityOgrn);
        }
        if (legalEntityInn) {
          formRef?.current.change(innFieldName, legalEntityInn);
        }
        if (legalEntityInn && legalEntityOgrn && checkOgrnBySubjectType(subjectType?.label, legalEntityOgrn)) {
          formRef?.current.change(isAutocompleteFieldName, true);
        }
        if (legalEntityOrgName) {
          formRef?.current.change(organizationNameFieldName, legalEntityOrgName);
        }
        if (legalEntityLeAddress && isUR) {
          formRef?.current.change(locationAddressFieldName, legalEntityLeAddress);
        }
        if (legalEntityWorkAddress) {
          formRef?.current.change(workAddressFieldName, legalEntityWorkAddress);
        }
      }
    },
    [formRef, subjectType, innFieldName, name, ogrnFieldName, organizationNameFieldName, isUR, isIP],
  );

  useEffect(() => {
    if (inn && ogrn) {
      dispatchKnmSetLegalEntityData({
        inn,
        ogrn,
      });
    }
  }, [dispatchKnmSetLegalEntityData, inn, ogrn]);

  const handleAutoCompleteClear = useCallback(() => {
    formRef?.current.change(ogrnFieldName);
    formRef?.current.change(innFieldName);
    formRef?.current.change(organizationNameFieldName);
    formRef?.current.change(locationAddressFieldName);
    formRef?.current.change(isAutocompleteFieldName, false);
    dispatchKnmSetLegalEntityData(undefined);
    clearItAccreditation();
  }, [dispatchKnmSetLegalEntityData, formRef, innFieldName, name, ogrnFieldName, organizationNameFieldName]);

  const handleRafpNzaChange = useCallback(
    (v: ChangeEvent<HTMLInputElement>) => {
      // Сбросить ИНН и КПП
      if (v.target.value) {
        formRef?.current.change(innFieldName, undefined);
        formRef?.current.change(kppFieldName, undefined);
        if (v.target.value !== rafpNza) {
          setRafpNzaNotFoundText(undefined);
        } else if (isRafpNzaNotFound) {
          setRafpNzaNotFoundText(rafpNotFoundMessage);
        }
      }
    },
    [formRef, innFieldName, kppFieldName],
  );

  const handleKppChange = useCallback(
    (v: ChangeEvent<HTMLInputElement>) => {
      if (v.target.value) {
        // Сбросить РАФП
        formRef?.current.change(rafpNzaFieldName, undefined);
      }
    },
    [formRef, rafpNzaFieldName],
  );

  useEffect(() => {
    if (!isAutocomplete) {
      if (isFIZ) {
        formRef?.current.change(ogrnFieldName);
      }
    }
  }, [formRef, isAutocomplete, isFIZ, ogrnFieldName]);

  const isFieldEnabled = !isWatchOnly && isSupervisor && isNewOrganizationTemplate;
  const isRafpNzaSent = organization.smevRequestStatus === RafpRequestStatusTypeEnum.REQUEST_SENT;
  const isRafpNzaNotFound = organization.smevRequestStatus === RafpRequestStatusTypeEnum.NOT_FOUND;
  const isRafpNzaError = organization.smevRequestStatus === RafpRequestStatusTypeEnum.ERROR_RETRY;
  const isInnFieldEnabled = isFieldEnabled || isRafpNzaNotFound;

  const isOrganizationNameFieldEnabled = isFIZ && isFieldEnabled;

  const getOrganizationNameFieldRequired = () => !isOrganizationNameFieldEnabled;

  const clearItAccreditation = useCallback(() => {
    formRef?.current.change(`${name}.isPresentInItRegistry`, undefined);
    formRef?.current.change(`${name}.itAccreditationResponse`, undefined);
  }, []);

  const handleSubjectTypeChange = useCallback(() => {
    formRef?.current.change(innFieldName, undefined);
    formRef?.current.change(ogrnFieldName, undefined);
    formRef?.current.change(rafpNzaFieldName, undefined);
    formRef?.current.change(kppFieldName, undefined);
    formRef?.current.change(organizationNameFieldName, undefined);
    formRef?.current.change(locationAddressFieldName, undefined);
    formRef?.current.change(snilsFieldName, undefined);
    formRef?.current.change(documentFieldName, undefined);
    formRef?.current.change(isAutocompleteFieldName, false);
    formRef?.current.change(countryFieldName, undefined);

    clearItAccreditation();
  }, [
    formRef,
    name,
    innFieldName,
    ogrnFieldName,
    rafpNzaFieldName,
    kppFieldName,
    organizationNameFieldName,
    countryFieldName,
  ]);

  const normalizeFio = (value: string | undefined) => {
    if (!value) return value;

    return value.replace(/[^а-яёa-z-`' ]/gi, '');
  };

  const isRafpFilled = !!rafpNza && /^\d{11}$/.test(rafpNza);

  const saveKnmAndWaitFromRafpWarning =
    (isRafpFilled || (inn && kpp)) && !organization.smevRequestStatus
      ? 'Сохраните КНМ и ожидайте загрузки информации из РАФП'
      : undefined;

  const waitFromRafpWarning = isRafpNzaSent ? 'Ожидайте загрузки информации из РАФП' : undefined;
  const rafpWarning = isFIUR ? saveKnmAndWaitFromRafpWarning || waitFromRafpWarning : undefined;

  const innOrSnilsWarning = isFIZ && !inn && !snils ? 'Необходимо заполнить либо ИНН, либо СНИЛС' : undefined;
  const filterUlCountries = (values: ICountry[]) => values?.filter(item => item.ulEnabled);
  const filterFlCountries = (values: ICountry[]) => values?.filter(item => item.flEnabled);

  return (
    <>
      <Row>
        <FieldBlock
          label={getTitleFieldWithRedStar({
            label: isNewOrganizationTemplate ? `Тип учредителя` : `Тип контролируемого лица`,
            field: subjectTypeFieldName,
            isKnm,
            isPm,
            isDecision,
          })}
          placementLabel="left"
          id={`${parentId}.subjectType`}
          color="black"
          className={styles.FieldBlock}
        >
          <SelectInputField
            name={subjectTypeFieldName}
            options={subjectTypeOptions}
            disabled={!isFieldEnabled}
            size="large"
            onChange={handleSubjectTypeChange}
          />
        </FieldBlock>
      </Row>

      {subjectType && (
        <>
          {((!isIUR && !isIFIZ) || isFIUR || isFIZ) && (
            <Row>
              {!isIUR && !isIFIZ && (
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: `Идентификационный номер налогоплательщика (ИНН)`,
                    field: innFieldName,
                    isKnm,
                    isPm,
                    isDecision,
                  })}
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  id={`${parentId}.inn`}
                >
                  <KnmInnField
                    name={innFieldName}
                    clearable={isInnFieldEnabled && isAutocomplete}
                    disabled={!isInnFieldEnabled || isAutocomplete || !!rafpNza}
                    inn={inn}
                    isFiz={isFIZ}
                    isShowAutoCompleteField={isIUR && !isFIUR}
                    onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                    onClear={handleAutoCompleteClear}
                    onLoadingChange={setFnsLookupByInnLoadingFlag}
                    warning={innOrSnilsWarning}
                    subjectType={subjectType}
                    isShowAdditionalMessages={true}
                    checkSubjectResult={organization.checkSubjectResult}
                  />
                  {isRafpNzaNotFound && inn && kpp && <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>}
                </FieldBlock>
              )}
              {isFIUR && (
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: `Номер записи об аккредитации в РАФП`,
                    field: rafpNzaFieldName,
                    isKnm,
                    isPm,
                    isDecision,
                  })}
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  id={`${parentId}.rafpNza`}
                >
                  <div className={styles.ColumnWrapper}>
                    <TextInputField
                      name={rafpNzaFieldName}
                      disabled={!!inn || !!kpp}
                      onChange={handleRafpNzaChange}
                      warning={rafpWarning}
                    />
                    {isRafpNzaNotFound && !inn && !kpp && (
                      <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>
                    )}
                    {isRafpNzaError && (
                      <Hint
                        text="Запрашиваемые сведения не могут быть предоставлены в настоящий момент. Автоматический запрос
                        будет произведен через час"
                      />
                    )}
                  </div>
                </FieldBlock>
              )}
              {isFIZ && (
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: `Страховой номер индивидуального лицевого счета (СНИЛС)`,
                    field: snilsFieldName,
                    isKnm,
                    isPm,
                    isDecision,
                  })}
                  placementLabel="left"
                  color="black"
                  className={styles.FieldBlock}
                  id={`${parentId}.snils`}
                >
                  <KnmSnilsField name={snilsFieldName} warning={innOrSnilsWarning} disabled={!isFieldEnabled} />
                </FieldBlock>
              )}
            </Row>
          )}
          {(isUR || isIP) && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: isIP
                    ? `Основной государственный регистрационный номер индивидуального предпринимателя (ОГРНИП)`
                    : `Основной государственный регистрационный номер (ОГРН)`,
                  field: ogrnFieldName,
                  isKnm,
                  isPm,
                  isDecision,
                })}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
                id={`${parentId}.ogrn`}
              >
                <KnmOgrnField
                  name={ogrnFieldName}
                  clearable={isFieldEnabled && isAutocomplete}
                  disabled={!isFieldEnabled || isAutocomplete}
                  ogrn={ogrn}
                  onAutoCompleteSelect={handleLegalEntityAutoCompleteSelect}
                  onClear={handleAutoCompleteClear}
                  onLoadingChange={setFnsLookupByOgrnLoadingFlag}
                />
              </FieldBlock>
            </Row>
          )}
          {isFIUR && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: `Код причины постановки на учет (КПП)`,
                  field: kppFieldName,
                  isKnm,
                  isPm,
                  isDecision,
                })}
                id={`${parentId}.kpp`}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField
                  name={kppFieldName}
                  disabled={isWatchOnly || !!rafpNza}
                  onChange={handleKppChange}
                  warning={rafpWarning}
                />
                {isRafpNzaNotFound && (!!inn || !!kpp) && (
                  <div className={styles.RafpNotFound}>{rafpNzaNotFoundText}</div>
                )}
              </FieldBlock>
            </Row>
          )}
          {(isUR || isIP || isFIUR) && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: `Наименование проверяемого лица`,
                  field: organizationNameFieldName,
                  isKnm,
                  isPm,
                  isDecision,
                })}
                id={`${parentId}.organizationName`}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextareaField
                  inputClassName={styles.OrganizationNameInput}
                  name={organizationNameFieldName}
                  resize="none"
                  clearable={isOrganizationNameFieldEnabled && isAutocomplete}
                  disabled={!isOrganizationNameFieldEnabled}
                  onClear={handleAutoCompleteClear}
                  validate={composeValidators(
                    makeRequired(REQUIRED_ERROR, getOrganizationNameFieldRequired),
                    makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
                  )}
                />
              </FieldBlock>
            </Row>
          )}
          {(isIFIZ || isFIZ) && (
            <Row>
              <FieldBlock
                id={`${parentId}.organizationName`}
                label={getTitleFieldWithRedStar({
                  label: `Фамилия, имя, отчество (при наличии)`,
                  field: organizationNameFieldName,
                  isKnm,
                  isPm,
                  isDecision,
                })}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={organizationNameFieldName} disabled={!isFieldEnabled} parse={normalizeFio} />
              </FieldBlock>
            </Row>
          )}
          {(isUR || isFIUR) && (
            <Row>
              <FieldBlock
                id={`${parentId}.locationAddress`}
                label={getTitleFieldWithRedStar({
                  label: `Адрес места нахождения`,
                  field: locationAddressFieldName,
                  isKnm,
                  isPm,
                  isDecision,
                })}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField name={locationAddressFieldName} disabled={true} />
              </FieldBlock>
            </Row>
          )}
          {isIFIZ && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: `Сведения о документе, удостоверяющем личность`,
                  field: documentFieldName,
                  isKnm,
                  isPm,
                  isDecision,
                })}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
                id={`${parentId}.document`}
              >
                <TextInputField name={documentFieldName} parse={parsePassport} disabled={!isFieldEnabled} />
              </FieldBlock>
            </Row>
          )}
          {isIUR && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: `Наименование`,
                  field: organizationNameFieldName,
                  isKnm,
                  isPm,
                  isDecision,
                })}
                id={`${parentId}.organizationName`}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <TextInputField
                  name={organizationNameFieldName}
                  validate={composeValidators(
                    makeRequired(REQUIRED_ERROR, getOrganizationNameFieldRequired),
                    makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
                  )}
                  disabled={!isFieldEnabled}
                />
              </FieldBlock>
            </Row>
          )}
          {(isIUR || isIFIZ) && (
            <Row>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: isIUR
                    ? 'Государство, в котором указанное юридическое лицо зарегистрировано'
                    : 'Гражданство (при наличии)',
                  field: countryFieldName,
                  isKnm,
                  isPm,
                  isDecision,
                })}
                placementLabel="left"
                color="black"
                className={styles.FieldBlock}
              >
                <SelectInputFieldAutoOptions
                  id={`${parentId}.country`}
                  name={countryFieldName}
                  isClearable={false}
                  size="medium"
                  optionName={CommonCatalogsNamesEnum.COUNTRIES}
                  disabled={!isFieldEnabled}
                  transformFunc={isIFIZ ? filterFlCountries : filterUlCountries}
                />
              </FieldBlock>
            </Row>
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IPetitionOrganizationStoreProps => ({
  formRefKnm: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  subjectTypeOptions: getTargetEntityFounderSelectOptions(state),
});

export const PetitionOrganization = connect(mapStateToProps)(PetitionOrganizationWithoutStore);
